export interface EpisodeData {
  title: string;
  author: string;
  description?: string;
  url: string;
  publishDate?: string;
  duration: number;
  transcript?: Array<{
    text: string;
    startTime: number;
    endTime: number;
    timestamp?: [number, number];
  }>;
  summary?: string;
  keyPoints?: string[];
  chapters?: Array<{
    title: string;
    startTime: number;
    endTime: number;
    content?: string;
  }>;
  vocabulary?: Array<{
    word: string;
    definition: string;
    context?: string;
  }>;
}

export interface ExportFormat {
  id: string;
  name: string;
  fileExtension: string;
  mimeType: string;
  category: 'document' | 'text' | 'media';
}

// Utility functions
export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Unknown date';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

// Export generators for Phase 1 formats
export const exportGenerators = {
  // Standard Markdown format
  markdown: async (data: EpisodeData): Promise<string> => {
    const sections = [];
    
    // Header
    sections.push(`# ${data.title}`);
    sections.push('');
    
    // Metadata
    sections.push(`**Author:** ${data.author}`);
    sections.push(`**Duration:** ${formatDuration(data.duration)}`);
    if (data.publishDate) {
      sections.push(`**Published:** ${formatDate(data.publishDate)}`);
    }
    sections.push(`**Source:** [Listen Here](${data.url})`);
    sections.push('');
    
    
    // Summary
    if (data.summary) {
      sections.push('## Summary');
      sections.push('');
      sections.push(data.summary);
      sections.push('');
    }
    
    // Key Points
    if (data.keyPoints && data.keyPoints.length > 0) {
      sections.push('## Key Takeaways');
      sections.push('');
      data.keyPoints.forEach((point, index) => {
        sections.push(`${index + 1}. ${point}`);
      });
      sections.push('');
    }
    
    // Chapters
    if (data.chapters && data.chapters.length > 0) {
      sections.push('## Chapters');
      sections.push('');
      data.chapters.forEach(chapter => {
        sections.push(`### [${formatTime(chapter.startTime)}] ${chapter.title}`);
        if (chapter.content) {
          sections.push('');
          sections.push(chapter.content);
        }
        sections.push('');
      });
    }
    
    // Vocabulary
    if (data.vocabulary && data.vocabulary.length > 0) {
      sections.push('## Vocabulary');
      sections.push('');
      data.vocabulary.forEach(term => {
        sections.push(`**${term.word}:** ${term.definition}`);
        if (term.context) {
          sections.push(`*Context: "${term.context}"*`);
        }
        sections.push('');
      });
    }
    
    // Transcript
    if (data.transcript && data.transcript.length > 0) {
      sections.push('## Full Transcript');
      sections.push('');
      data.transcript.forEach(segment => {
        const timestamp = segment.timestamp ? segment.timestamp[0] : segment.startTime;
        sections.push(`**[${formatTime(timestamp)}]** ${segment.text}`);
        sections.push('');
      });
    }
    
    // Footer
    sections.push('---');
    sections.push(`*Exported on ${formatDate(new Date().toISOString())}*`);
    
    return sections.join('\n');
  },

  // Plain text format
  text: async (data: EpisodeData): Promise<string> => {
    const sections = [];
    
    // Header
    sections.push(data.title.toUpperCase());
    sections.push('='.repeat(data.title.length));
    sections.push('');
    
    // Metadata
    sections.push(`Author: ${data.author}`);
    sections.push(`Duration: ${formatDuration(data.duration)}`);
    if (data.publishDate) {
      sections.push(`Published: ${formatDate(data.publishDate)}`);
    }
    sections.push(`Source: ${data.url}`);
    sections.push('');
    
    
    // Summary
    if (data.summary) {
      sections.push('SUMMARY');
      sections.push('-'.repeat(7));
      sections.push(data.summary);
      sections.push('');
    }
    
    // Key Points
    if (data.keyPoints && data.keyPoints.length > 0) {
      sections.push('KEY TAKEAWAYS');
      sections.push('-'.repeat(13));
      data.keyPoints.forEach((point, index) => {
        sections.push(`${index + 1}. ${point}`);
      });
      sections.push('');
    }
    
    // Chapters
    if (data.chapters && data.chapters.length > 0) {
      sections.push('CHAPTERS');
      sections.push('-'.repeat(8));
      data.chapters.forEach(chapter => {
        sections.push(`[${formatTime(chapter.startTime)}] ${chapter.title}`);
        if (chapter.content) {
          sections.push(chapter.content);
        }
        sections.push('');
      });
    }
    
    // Transcript
    if (data.transcript && data.transcript.length > 0) {
      sections.push('FULL TRANSCRIPT');
      sections.push('-'.repeat(15));
      data.transcript.forEach(segment => {
        const timestamp = segment.timestamp ? segment.timestamp[0] : segment.startTime;
        sections.push(`[${formatTime(timestamp)}] ${segment.text}`);
      });
      sections.push('');
    }
    
    return sections.join('\n');
  },

  // JSON format for programmatic use
  json: async (data: EpisodeData): Promise<string> => {
    const exportData = {
      meta: {
        exportedAt: new Date().toISOString(),
        format: 'episode-json',
        version: '1.0'
      },
      episode: {
        title: data.title,
        author: data.author,
        url: data.url,
        publishDate: data.publishDate,
        duration: data.duration,
        formattedDuration: formatDuration(data.duration)
      },
      content: {
        summary: data.summary,
        keyPoints: data.keyPoints || [],
        chapters: data.chapters?.map(chapter => ({
          ...chapter,
          formattedStartTime: formatTime(chapter.startTime),
          formattedEndTime: formatTime(chapter.endTime)
        })) || [],
        vocabulary: data.vocabulary || [],
        transcript: data.transcript?.map(segment => ({
          ...segment,
          formattedStartTime: formatTime(segment.timestamp ? segment.timestamp[0] : segment.startTime),
          formattedEndTime: formatTime(segment.timestamp ? segment.timestamp[1] : segment.endTime)
        })) || []
      }
    };
    
    return JSON.stringify(exportData, null, 2);
  }
};

// File download utility
export const downloadFile = (content: string, filename: string, mimeType: string = 'text/plain') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// Copy to clipboard utility
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      textArea.remove();
      return success;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

// PDF generation utility (using jsPDF - you'll need to install it)
export const generatePDF = async (data: EpisodeData): Promise<Blob> => {
  // For now, we'll create a simple text-based PDF
  // In a real implementation, you'd use jsPDF or similar
  const markdownContent = await exportGenerators.markdown(data);
  return new Blob([markdownContent], { type: 'application/pdf' });
};

export const exportFormats: ExportFormat[] = [
  {
    id: 'markdown',
    name: 'Markdown',
    fileExtension: 'md',
    mimeType: 'text/markdown',
    category: 'document'
  },
  {
    id: 'text',
    name: 'Plain Text',
    fileExtension: 'txt',
    mimeType: 'text/plain',
    category: 'text'
  },
  {
    id: 'json',
    name: 'JSON',
    fileExtension: 'json',
    mimeType: 'application/json',
    category: 'document'
  }
];